/**
 * Gym route — Analytics dashboard for Hevy workout data.
 * Import-driven: CSV from Hevy or paste text. No live tracking.
 */

import { useState, useMemo, type DragEvent, type ChangeEvent } from "react";
import {
  LineChart, Line, BarChart, Bar, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, Area, AreaChart,
} from "recharts";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";
import { HexBadge } from "../components/ui/HexBadge";
import {
  useSummaryStats,
  useInsights,
  useExerciseProgression,
  useGymImport,
  useGymImportText,
  useHeatmap,
  useEstimated1RM,
  useWeeklyVolume,
  useExercises,
  useBiometrics,
  useUpsertBiometrics,
} from "../hooks/useGym";
import {
  TrendingUp,
  Flame,
  Upload,
  CalendarDays,
  BarChart3,
  Activity,
  Award,
  AlertTriangle,
  ClipboardPaste,
  FileText,
  Ruler,
  Plus,
  Scale,
} from "lucide-react";

type Tab = "dashboard" | "progression" | "medidas" | "import";

// ============================================================================
// Helpers
// ============================================================================

function lbsFromKg(kg: number): number { return +(kg * 2.20462).toFixed(1); }

function formatVolume(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toFixed(0);
}

const ACCENT = "var(--bw-accent)";
const DANGER = "var(--bw-danger)";
const SUCCESS = "var(--bw-success)";
const WARN = "var(--bw-warn)";
const MUTED = "var(--bw-muted)";
const FG = "var(--bw-fg)";

const MUSCLE_COLORS: Record<string, string> = {
  chest: "#ff003c",
  back: "#00f0ff",
  legs: "#00ff9c",
  shoulders: "#fcee0a",
  arms: "#ff00ff",
  core: "#ff8800",
  cardio: "#0066ff",
  other: "#6a6a7e",
};

// ============================================================================
// Dashboard Tab
// ============================================================================

function DashboardTab() {
  const { data: stats } = useSummaryStats();
  const { data: insights } = useInsights();
  const { data: heatmapData } = useHeatmap();
  const { data: weeklyData } = useWeeklyVolume(16);
  const { data: e1rmData } = useEstimated1RM();

  const heatmap = heatmapData?.heatmap ?? [];
  const weekly = weeklyData?.weeks ?? [];
  const e1rmExercises = e1rmData?.exercises ?? [];

  // Top 5 1RM exercises
  const top1RM = useMemo(
    () => [...e1rmExercises].sort((a, b) => b.current_e1rm - a.current_e1rm).slice(0, 5),
    [e1rmExercises],
  );

  // Muscle volume for radar chart
  const radarData = useMemo(() => {
    if (!insights?.volume_30d) return [];
    return Object.entries(insights.volume_30d)
      .filter(([g]) => g !== "other" && g !== "cardio")
      .map(([group, volume]) => ({
        group: group.charAt(0).toUpperCase() + group.slice(1),
        volume: volume as number,
      }));
  }, [insights]);

  // Heatmap: last 90 days as grid
  const heatmapGrid = useMemo(() => {
    const map = new Map(heatmap.map((h) => [h.date, h]));
    const days: { date: string; level: number }[] = [];
    const now = new Date();
    for (let i = 89; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split("T")[0]!;
      const entry = map.get(dateStr);
      const level = entry ? Math.min(4, Math.ceil(entry.volume / 5000)) : 0;
      days.push({ date: dateStr, level });
    }
    return days;
  }, [heatmap]);

  return (
    <div className="space-y-6">
      {/* Summary stats row */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <CorpoCard>
          <div className="flex items-center gap-3">
            <Flame size={20} className="text-[var(--bw-danger)]" />
            <div>
              <p className="text-xs text-[var(--bw-muted)] uppercase">Streak</p>
              <p className="text-2xl font-bold text-[var(--bw-fg)]">
                {insights?.consistency.streak ?? 0}
                <span className="text-sm text-[var(--bw-muted)] ml-1">días</span>
              </p>
            </div>
          </div>
        </CorpoCard>

        <CorpoCard>
          <div className="flex items-center gap-3">
            <CalendarDays size={20} className="text-[var(--bw-accent)]" />
            <div>
              <p className="text-xs text-[var(--bw-muted)] uppercase">Sesiones / sem</p>
              <p className="text-2xl font-bold text-[var(--bw-fg)]">
                {insights?.consistency.avg_per_week ?? 0}
              </p>
            </div>
          </div>
        </CorpoCard>

        <CorpoCard>
          <div className="flex items-center gap-3">
            <BarChart3 size={20} className="text-[var(--bw-success)]" />
            <div>
              <p className="text-xs text-[var(--bw-muted)] uppercase">Vol. semanal</p>
              <p className="text-2xl font-bold text-[var(--bw-fg)]">
                {formatVolume(stats?.weeklyVolume ?? 0)}
                <span className="text-sm text-[var(--bw-muted)] ml-1">lbs</span>
              </p>
            </div>
          </div>
        </CorpoCard>

        <CorpoCard>
          <div className="flex items-center gap-3">
            <Award size={20} className="text-[var(--bw-warn)]" />
            <div>
              <p className="text-xs text-[var(--bw-muted)] uppercase">PRs</p>
              <p className="text-2xl font-bold text-[var(--bw-fg)]">
                {insights?.prs.length ?? 0}
              </p>
            </div>
          </div>
        </CorpoCard>
      </div>

      {/* Frequency heatmap */}
      <CorpoCard title="FRECUENCIA (90 DÍAS)">
        <div className="flex flex-wrap gap-[3px]">
          {heatmapGrid.map((day) => (
            <div
              key={day.date}
              className="w-3 h-3 md:w-[14px] md:h-[14px] border border-[var(--bw-border)]"
              style={{
                backgroundColor: day.level === 0
                  ? "var(--bw-surface-2)"
                  : `color-mix(in srgb, var(--bw-accent) ${day.level * 25}%, var(--bw-surface-2))`,
                boxShadow: day.level >= 3 ? "0 0 4px var(--bw-accent)" : "none",
              }}
              title={`${day.date} — ${day.level > 0 ? "Entrenamiento" : "Descanso"}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2 text-[10px] text-[var(--bw-muted)]">
          <span>Menos</span>
          {[0, 1, 2, 3, 4].map((l) => (
            <div
              key={l}
              className="w-3 h-3 border border-[var(--bw-border)]"
              style={{
                backgroundColor: l === 0
                  ? "var(--bw-surface-2)"
                  : `color-mix(in srgb, var(--bw-accent) ${l * 25}%, var(--bw-surface-2))`,
              }}
            />
          ))}
          <span>Más</span>
        </div>
      </CorpoCard>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Weekly volume chart */}
        <CorpoCard title="VOLUMEN SEMANAL">
          {weekly.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={weekly}>
                <defs>
                  <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--bw-accent)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--bw-accent)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--bw-border)" />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: "var(--bw-muted)" }} tickFormatter={(w: string) => w.split("-W")[1] ?? w} />
                <YAxis tick={{ fontSize: 10, fill: "var(--bw-muted)" }} tickFormatter={formatVolume} />
                <Tooltip
                  contentStyle={{ background: "var(--bw-surface)", border: "1px solid var(--bw-border)", color: "var(--bw-fg)", fontSize: 12 }}
                  formatter={(v) => [`${formatVolume(Number(v ?? 0))} lbs`, "Volumen"]}
                />
                <Area type="monotone" dataKey="volume" stroke="var(--bw-accent)" fill="url(#volGrad)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-[var(--bw-muted)] py-8 text-center">Sin datos</p>
          )}
        </CorpoCard>

        {/* Muscle balance radar */}
        <CorpoCard title="BALANCE MUSCULAR (30D)">
          {radarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <RadarChart data={radarData} outerRadius="70%">
                <PolarGrid stroke="var(--bw-border)" />
                <PolarAngleAxis dataKey="group" tick={{ fontSize: 10, fill: "var(--bw-muted)" }} />
                <PolarRadiusAxis tick={false} axisLine={false} />
                <Radar dataKey="volume" stroke="var(--bw-accent)" fill="var(--bw-accent)" fillOpacity={0.2} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-[var(--bw-muted)] py-8 text-center">Sin datos</p>
          )}
        </CorpoCard>
      </div>

      {/* Top estimated 1RM */}
      <CorpoCard title="TOP 1RM ESTIMADO (EPLEY)">
        {top1RM.length > 0 ? (
          <div className="space-y-2">
            {top1RM.map((ex, i) => (
              <div key={ex.exercise_id} className="flex items-center gap-3">
                <span className="text-xs text-[var(--bw-muted)] w-5 text-right font-mono">
                  {i + 1}.
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[var(--bw-fg)] truncate">{ex.exercise_name}</p>
                </div>
                <HexBadge variant="primary">
                  {lbsFromKg(ex.current_e1rm)} lbs
                </HexBadge>
                {ex.peak_e1rm > ex.current_e1rm && (
                  <span className="text-[10px] text-[var(--bw-muted)]">
                    peak: {lbsFromKg(ex.peak_e1rm)}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[var(--bw-muted)] py-4 text-center">Sin datos</p>
        )}
      </CorpoCard>

      {/* PRs + Plateaus side by side */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* PRs */}
        <CorpoCard title="PERSONAL RECORDS">
          {(insights?.prs ?? []).length > 0 ? (
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {(insights?.prs ?? []).slice(0, 15).map((pr) => (
                <div key={pr.exercise_id} className="flex items-center justify-between py-1 border-b border-[var(--bw-border)] last:border-0">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-[var(--bw-fg)] truncate">{pr.exercise_name}</p>
                    <p className="text-[10px] text-[var(--bw-muted)]">{pr.best_date}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-[var(--bw-accent)]">
                      {lbsFromKg(pr.max_weight_kg)} lbs
                    </span>
                    <span className="text-[10px] text-[var(--bw-muted)] ml-1">
                      x{pr.max_reps}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--bw-muted)] py-4 text-center">Sin PRs registrados</p>
          )}
        </CorpoCard>

        {/* Plateaus */}
        <CorpoCard title="ESTANCAMIENTOS">
          {(insights?.plateaus ?? []).length > 0 ? (
            <div className="space-y-2">
              {(insights?.plateaus ?? []).map((p) => (
                <div key={p.exercise_id} className="flex items-center gap-2 py-1">
                  <AlertTriangle size={14} className="text-[var(--bw-warn)] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[var(--bw-fg)] truncate">{p.exercise_name}</p>
                  </div>
                  <HexBadge variant="warning">
                    {p.weeks_stagnant} sem
                  </HexBadge>
                  <span className="text-[10px] text-[var(--bw-muted)]">
                    @ {lbsFromKg(p.max_weight_kg)} lbs
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--bw-success)] py-4 text-center">Sin estancamientos detectados</p>
          )}
        </CorpoCard>
      </div>

      {/* Progression suggestions */}
      {(insights?.suggestions ?? []).length > 0 && (
        <CorpoCard title="SUGERENCIAS DE PROGRESIÓN">
          <div className="space-y-2">
            {(insights?.suggestions ?? []).map((s) => (
              <div key={s.exercise_id} className="flex items-center gap-3 py-1">
                <TrendingUp size={14} className="text-[var(--bw-success)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[var(--bw-fg)] truncate">{s.exercise_name}</p>
                </div>
                <span className="text-[10px] text-[var(--bw-muted)]">
                  {lbsFromKg(s.current_weight_kg)} →
                </span>
                <HexBadge variant="success">
                  {lbsFromKg(s.suggested_weight_kg)} lbs
                </HexBadge>
              </div>
            ))}
          </div>
        </CorpoCard>
      )}
    </div>
  );
}

// ============================================================================
// Progression Tab — exercise-level charts
// ============================================================================

function ProgressionTab() {
  const { data: exercisesData } = useExercises();
  const { data: e1rmData } = useEstimated1RM();
  const [selectedExercise, setSelectedExercise] = useState<number | null>(null);
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const { data: progression } = useExerciseProgression(selectedExercise);

  const exercises = exercisesData ?? [];
  const e1rmExercises = e1rmData?.exercises ?? [];

  // Only show exercises that have data
  const exercisesWithData = useMemo(() => {
    const withDataIds = new Set(e1rmExercises.map((e) => e.exercise_id));
    return exercises.filter((e) => withDataIds.has(e.id));
  }, [exercises, e1rmExercises]);

  const filteredExercises = useMemo(
    () => groupFilter === "all" ? exercisesWithData : exercisesWithData.filter((e) => e.muscle_group === groupFilter),
    [exercisesWithData, groupFilter],
  );

  const groups = useMemo(
    () => [...new Set(exercisesWithData.map((e) => e.muscle_group))].sort(),
    [exercisesWithData],
  );

  // Get 1RM progression for selected exercise
  const e1rmProgression = useMemo(() => {
    if (!selectedExercise) return [];
    const ex = e1rmExercises.find((e) => e.exercise_id === selectedExercise);
    return ex?.progression ?? [];
  }, [selectedExercise, e1rmExercises]);

  // Volume progression (from standard endpoint)
  const volumeData = useMemo(() => {
    if (!progression) return [];
    return (progression as Array<{ date: string; max_weight: number; max_reps: number; total_volume: number }>).map((p) => ({
      date: p.date,
      weight: lbsFromKg(p.max_weight),
      reps: p.max_reps,
      volume: lbsFromKg(p.total_volume),
      e1rm: e1rmProgression.find((e) => e.date === p.date)?.e1rm
        ? lbsFromKg(e1rmProgression.find((e) => e.date === p.date)!.e1rm)
        : null,
    }));
  }, [progression, e1rmProgression]);

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex gap-2 flex-wrap">
        <select
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
          className="bg-[var(--bw-surface)] border border-[var(--bw-border)] text-[var(--bw-fg)] text-xs px-3 py-2 font-mono uppercase"
        >
          <option value="all">Todos</option>
          {groups.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
      </div>

      {/* Exercise list */}
      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
        {filteredExercises.map((ex) => {
          const e1rm = e1rmExercises.find((e) => e.exercise_id === ex.id);
          return (
            <div
              key={ex.id}
              onClick={() => setSelectedExercise(ex.id)}
              className={`
                p-3 border cursor-pointer transition-all duration-150
                ${selectedExercise === ex.id
                  ? "border-[var(--bw-accent)] bg-[var(--bw-accent)]/10 shadow-[var(--bw-glow)]"
                  : "border-[var(--bw-border)] bg-[var(--bw-surface)] hover:border-[var(--bw-accent)]"
                }
              `}
            >
              <div className="flex items-center justify-between">
                <p className="text-xs text-[var(--bw-fg)] truncate flex-1">{ex.name}</p>
                <HexBadge variant="muted" size="sm">
                  {ex.muscle_group}
                </HexBadge>
              </div>
              {e1rm && (
                <p className="text-[10px] text-[var(--bw-accent)] mt-1 font-mono">
                  1RM: {lbsFromKg(e1rm.current_e1rm)} lbs
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Progression chart */}
      {selectedExercise && volumeData.length > 0 && (
        <CorpoCard title={`PROGRESIÓN — ${exercises.find((e) => e.id === selectedExercise)?.name ?? ""}`}>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={volumeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--bw-border)" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--bw-muted)" }} />
              <YAxis yAxisId="weight" tick={{ fontSize: 10, fill: "var(--bw-muted)" }} />
              <YAxis yAxisId="reps" orientation="right" tick={{ fontSize: 10, fill: "var(--bw-muted)" }} />
              <Tooltip
                contentStyle={{ background: "var(--bw-surface)", border: "1px solid var(--bw-border)", color: "var(--bw-fg)", fontSize: 11 }}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line yAxisId="weight" type="monotone" dataKey="weight" stroke="var(--bw-accent)" strokeWidth={2} dot={false} name="Peso (lbs)" />
              <Line yAxisId="weight" type="monotone" dataKey="e1rm" stroke="var(--bw-warn)" strokeWidth={1} strokeDasharray="5 5" dot={false} name="1RM est." />
              <Line yAxisId="reps" type="monotone" dataKey="reps" stroke="var(--bw-success)" strokeWidth={1} dot={false} name="Reps" />
            </LineChart>
          </ResponsiveContainer>
        </CorpoCard>
      )}

      {selectedExercise && volumeData.length === 0 && (
        <CorpoCard>
          <p className="text-sm text-[var(--bw-muted)] text-center py-8">
            Sin datos de progresión para este ejercicio
          </p>
        </CorpoCard>
      )}
    </div>
  );
}

// ============================================================================
// Import Tab
// ============================================================================

function ImportTab() {
  const importCsv = useGymImport();
  const importText = useGymImportText();
  const [mode, setMode] = useState<"csv" | "text">("csv");
  const [textInput, setTextInput] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState<{ sessions_imported: number; sets_imported: number; exercises_created: number; errors: string[] } | null>(null);

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    const text = await file.text();
    importCsv.mutate(text, {
      onSuccess: (data) => setResult(data),
    });
  };

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    importCsv.mutate(text, {
      onSuccess: (data) => setResult(data),
    });
    e.target.value = "";
  };

  const handleTextImport = () => {
    if (!textInput.trim()) return;
    importText.mutate(textInput, {
      onSuccess: (data) => {
        setResult(data);
        setTextInput("");
      },
    });
  };

  return (
    <div className="space-y-4 max-w-2xl">
      {/* Mode toggle */}
      <div className="flex gap-2">
        <GlyphButton
          variant={mode === "csv" ? "primary" : "secondary"}
          size="sm"
          icon={<FileText size={14} />}
          onClick={() => setMode("csv")}
        >
          CSV de Hevy
        </GlyphButton>
        <GlyphButton
          variant={mode === "text" ? "primary" : "secondary"}
          size="sm"
          icon={<ClipboardPaste size={14} />}
          onClick={() => setMode("text")}
        >
          Pegar texto
        </GlyphButton>
      </div>

      {mode === "csv" ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`
            border-2 border-dashed p-8 text-center cursor-pointer transition-colors
            ${dragOver
              ? "border-[var(--bw-accent)] bg-[var(--bw-accent)]/5"
              : "border-[var(--bw-border)] hover:border-[var(--bw-accent)]"
            }
          `}
          onClick={() => document.getElementById("gym-csv-input")?.click()}
        >
          <Upload size={32} className="mx-auto text-[var(--bw-muted)] mb-3" />
          <p className="text-sm text-[var(--bw-fg)]">
            {importCsv.isPending ? "Importando..." : "Arrastra el CSV de Hevy aquí o haz clic"}
          </p>
          <p className="text-xs text-[var(--bw-muted)] mt-1">
            Exporta desde Hevy → Settings → Export Data
          </p>
          <input
            id="gym-csv-input"
            type="file"
            accept=".csv,text/csv,text/plain,text/comma-separated-values,application/csv"
            className="hidden"
            onChange={handleFileSelect}
          />
        </div>
      ) : (
        <div className="space-y-3">
          <textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder={`4. Brazo\nsábado, abr 18, 2026 at 12:07pm\n\nPress de Hombros (Barra)\nSerie 1: 95 lbs x 8\nSerie 2: 85 lbs x 4`}
            rows={12}
            className="
              w-full bg-[var(--bw-surface)] border border-[var(--bw-border)]
              text-[var(--bw-fg)] text-sm font-mono p-3
              placeholder-[var(--bw-muted)]
              focus:border-[var(--bw-accent)] focus:outline-none
              resize-y
            "
          />
          <GlyphButton
            onClick={handleTextImport}
            disabled={importText.isPending || !textInput.trim()}
            icon={<ClipboardPaste size={14} />}
          >
            {importText.isPending ? "Importando..." : "Importar texto"}
          </GlyphButton>
        </div>
      )}

      {/* Result */}
      {result && (
        <CorpoCard accent>
          <div className="space-y-1 text-sm">
            <p className="text-[var(--bw-success)]">
              {result.sessions_imported} sesiones · {result.sets_imported} sets · {result.exercises_created} ejercicios nuevos
            </p>
            {result.errors.length > 0 && (
              <div className="text-[var(--bw-danger)] text-xs">
                {result.errors.map((e, i) => <p key={i}>⚠ {e}</p>)}
              </div>
            )}
          </div>
        </CorpoCard>
      )}
    </div>
  );
}

// ============================================================================
// Medidas Tab — body measurements tracking + analysis
// ============================================================================

function MedidasTab() {
  const { data: bioData } = useBiometrics();
  const { data: weeklyData } = useWeeklyVolume(52);
  const upsertBio = useUpsertBiometrics();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0]!,
    weight_kg: "",
    body_fat_pct: "",
    waist_cm: "",
    neck_cm: "",
    chest_cm: "",
    navel_cm: "",
    arm_l_cm: "",
    arm_r_cm: "",
    leg_l_cm: "",
    leg_r_cm: "",
    resting_hr: "",
    sleep_hours: "",
    notes: "",
  });

  const biometrics = bioData ?? [];
  const weekly = weeklyData?.weeks ?? [];

  // Weight trend chart data (merge biometrics + volume)
  const weightTrend = useMemo(() => {
    if (!biometrics.length) return [];
    return [...biometrics].reverse().map((b) => ({
      date: b.date,
      weight: b.weight_kg ? lbsFromKg(b.weight_kg) : null,
      bodyFat: b.body_fat_pct,
      waist: b.waist_cm,
      restHR: b.resting_hr,
      sleep: b.sleep_hours,
    }));
  }, [biometrics]);

  // Correlation data: weight vs volume (merge by week)
  const correlationData = useMemo(() => {
    if (!biometrics.length || !weekly.length) return [];
    // Group biometrics by ISO week
    const bioByWeek = new Map<string, number>();
    for (const b of biometrics) {
      if (!b.weight_kg) continue;
      const d = new Date(b.date);
      const weekNum = `${d.getFullYear()}-W${String(Math.ceil((d.getTime() - new Date(d.getFullYear(), 0, 1).getTime()) / 604800000)).padStart(2, "0")}`;
      bioByWeek.set(weekNum, lbsFromKg(b.weight_kg));
    }
    return weekly
      .filter((w) => bioByWeek.has(w.week))
      .map((w) => ({
        week: w.week,
        volume: w.volume,
        weight: bioByWeek.get(w.week)!,
        sessions: w.sessions,
      }));
  }, [biometrics, weekly]);

  // Stats
  const latest = biometrics[0];
  const oldest = biometrics[biometrics.length - 1];
  const weightChange = latest?.weight_kg && oldest?.weight_kg
    ? +(latest.weight_kg - oldest.weight_kg).toFixed(1)
    : null;

  const handleSubmit = () => {
    const data: Record<string, unknown> = { date: form.date };
    if (form.weight_kg) data.weight_kg = parseFloat(form.weight_kg);
    if (form.body_fat_pct) data.body_fat_pct = parseFloat(form.body_fat_pct);
    if (form.waist_cm) data.waist_cm = parseFloat(form.waist_cm);
    if (form.neck_cm) data.neck_cm = parseFloat(form.neck_cm);
    if (form.chest_cm) data.chest_cm = parseFloat(form.chest_cm);
    if (form.navel_cm) data.navel_cm = parseFloat(form.navel_cm);
    if (form.arm_l_cm) data.arm_l_cm = parseFloat(form.arm_l_cm);
    if (form.arm_r_cm) data.arm_r_cm = parseFloat(form.arm_r_cm);
    if (form.leg_l_cm) data.leg_l_cm = parseFloat(form.leg_l_cm);
    if (form.leg_r_cm) data.leg_r_cm = parseFloat(form.leg_r_cm);
    if (form.resting_hr) data.resting_hr = parseInt(form.resting_hr);
    if (form.sleep_hours) data.sleep_hours = parseFloat(form.sleep_hours);
    if (form.notes) data.notes = form.notes;

    upsertBio.mutate(data as Parameters<typeof upsertBio.mutate>[0], {
      onSuccess: () => {
        setShowForm(false);
        setForm((f) => ({
          ...f,
          weight_kg: "",
          body_fat_pct: "",
          waist_cm: "",
          neck_cm: "",
          chest_cm: "",
          navel_cm: "",
          arm_l_cm: "",
          arm_r_cm: "",
          leg_l_cm: "",
          leg_r_cm: "",
          resting_hr: "",
          sleep_hours: "",
          notes: "",
        }));
      },
    });
  };

  const inputClass = "bg-[var(--bw-surface)] border border-[var(--bw-border)] text-[var(--bw-fg)] text-sm font-mono px-3 py-2 w-full focus:border-[var(--bw-accent)] focus:outline-none";

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <CorpoCard>
          <div className="flex items-center gap-3">
            <Scale size={20} className="text-[var(--bw-accent)]" />
            <div>
              <p className="text-xs text-[var(--bw-muted)] uppercase">Peso actual</p>
              <p className="text-2xl font-bold text-[var(--bw-fg)]">
                {latest?.weight_kg ? `${lbsFromKg(latest.weight_kg)}` : "—"}
                <span className="text-sm text-[var(--bw-muted)] ml-1">lbs</span>
              </p>
            </div>
          </div>
        </CorpoCard>

        <CorpoCard>
          <div>
            <p className="text-xs text-[var(--bw-muted)] uppercase">Cambio total</p>
            <p className={`text-2xl font-bold ${weightChange && weightChange < 0 ? "text-[var(--bw-success)]" : weightChange && weightChange > 0 ? "text-[var(--bw-warn)]" : "text-[var(--bw-fg)]"}`}>
              {weightChange !== null ? `${weightChange > 0 ? "+" : ""}${lbsFromKg(weightChange)} lbs` : "—"}
            </p>
          </div>
        </CorpoCard>

        <CorpoCard>
          <div>
            <p className="text-xs text-[var(--bw-muted)] uppercase">Body fat</p>
            <p className="text-2xl font-bold text-[var(--bw-fg)]">
              {latest?.body_fat_pct ? `${latest.body_fat_pct}%` : "—"}
            </p>
          </div>
        </CorpoCard>

        <CorpoCard>
          <div>
            <p className="text-xs text-[var(--bw-muted)] uppercase">Cintura</p>
            <p className="text-2xl font-bold text-[var(--bw-fg)]">
              {latest?.waist_cm ? `${latest.waist_cm} cm` : "—"}
            </p>
          </div>
        </CorpoCard>
      </div>

      {/* Add measurement button / form */}
      {!showForm ? (
        <GlyphButton variant="secondary" size="sm" icon={<Plus size={14} />} onClick={() => setShowForm(true)}>
          Registrar medidas
        </GlyphButton>
      ) : (
        <CorpoCard title="NUEVA MEDIDA" accent>
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Fecha</label>
              <input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Peso (kg)</label>
              <input type="number" step="0.1" placeholder="80.0" value={form.weight_kg} onChange={(e) => setForm((f) => ({ ...f, weight_kg: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Body fat %</label>
              <input type="number" step="0.1" placeholder="20.0" value={form.body_fat_pct} onChange={(e) => setForm((f) => ({ ...f, body_fat_pct: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Cintura (cm)</label>
              <input type="number" step="0.1" placeholder="85" value={form.waist_cm} onChange={(e) => setForm((f) => ({ ...f, waist_cm: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Cuello (cm)</label>
              <input type="number" step="0.1" placeholder="45" value={form.neck_cm} onChange={(e) => setForm((f) => ({ ...f, neck_cm: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Pecho (cm)</label>
              <input type="number" step="0.1" placeholder="100" value={form.chest_cm} onChange={(e) => setForm((f) => ({ ...f, chest_cm: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Ombligo (cm)</label>
              <input type="number" step="0.1" placeholder="120" value={form.navel_cm} onChange={(e) => setForm((f) => ({ ...f, navel_cm: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Bíceps izq (cm)</label>
              <input type="number" step="0.1" placeholder="37" value={form.arm_l_cm} onChange={(e) => setForm((f) => ({ ...f, arm_l_cm: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Bíceps der (cm)</label>
              <input type="number" step="0.1" placeholder="37" value={form.arm_r_cm} onChange={(e) => setForm((f) => ({ ...f, arm_r_cm: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Pierna izq (cm)</label>
              <input type="number" step="0.1" placeholder="60" value={form.leg_l_cm} onChange={(e) => setForm((f) => ({ ...f, leg_l_cm: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Pierna der (cm)</label>
              <input type="number" step="0.1" placeholder="60" value={form.leg_r_cm} onChange={(e) => setForm((f) => ({ ...f, leg_r_cm: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">FC reposo</label>
              <input type="number" placeholder="65" value={form.resting_hr} onChange={(e) => setForm((f) => ({ ...f, resting_hr: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Horas sueño</label>
              <input type="number" step="0.5" placeholder="7.5" value={form.sleep_hours} onChange={(e) => setForm((f) => ({ ...f, sleep_hours: e.target.value }))} className={inputClass} />
            </div>
          </div>
          <div className="mt-3">
            <label className="text-[10px] text-[var(--bw-muted)] uppercase block mb-1">Notas</label>
            <input type="text" placeholder="Notas opcionales..." value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={inputClass} />
          </div>
          <div className="flex gap-2 mt-3">
            <GlyphButton size="sm" onClick={handleSubmit} disabled={upsertBio.isPending}>
              {upsertBio.isPending ? "Guardando..." : "Guardar"}
            </GlyphButton>
            <GlyphButton variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              Cancelar
            </GlyphButton>
          </div>
        </CorpoCard>
      )}

      {/* Weight trend chart */}
      {weightTrend.length > 1 && (
        <CorpoCard title="TENDENCIA DE PESO">
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={weightTrend}>
              <defs>
                <linearGradient id="weightGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--bw-accent)" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="var(--bw-accent)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--bw-border)" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--bw-muted)" }} />
              <YAxis tick={{ fontSize: 10, fill: "var(--bw-muted)" }} domain={["dataMin - 5", "dataMax + 5"]} />
              <Tooltip contentStyle={{ background: "var(--bw-surface)", border: "1px solid var(--bw-border)", color: "var(--bw-fg)", fontSize: 11 }} />
              <Area type="monotone" dataKey="weight" stroke="var(--bw-accent)" fill="url(#weightGrad)" strokeWidth={2} name="Peso (lbs)" connectNulls />
            </AreaChart>
          </ResponsiveContainer>
        </CorpoCard>
      )}

      {/* Body composition charts side by side */}
      {weightTrend.length > 1 && weightTrend.some((w) => w.bodyFat || w.waist) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {weightTrend.some((w) => w.bodyFat) && (
            <CorpoCard title="BODY FAT %">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={weightTrend.filter((w) => w.bodyFat)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--bw-border)" />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--bw-muted)" }} />
                  <YAxis tick={{ fontSize: 10, fill: "var(--bw-muted)" }} domain={["dataMin - 2", "dataMax + 2"]} />
                  <Tooltip contentStyle={{ background: "var(--bw-surface)", border: "1px solid var(--bw-border)", color: "var(--bw-fg)", fontSize: 11 }} />
                  <Line type="monotone" dataKey="bodyFat" stroke="var(--bw-warn)" strokeWidth={2} dot={{ r: 3 }} name="Body fat %" />
                </LineChart>
              </ResponsiveContainer>
            </CorpoCard>
          )}

          {weightTrend.some((w) => w.waist) && (
            <CorpoCard title="CINTURA (CM)">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={weightTrend.filter((w) => w.waist)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--bw-border)" />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--bw-muted)" }} />
                  <YAxis tick={{ fontSize: 10, fill: "var(--bw-muted)" }} domain={["dataMin - 2", "dataMax + 2"]} />
                  <Tooltip contentStyle={{ background: "var(--bw-surface)", border: "1px solid var(--bw-border)", color: "var(--bw-fg)", fontSize: 11 }} />
                  <Line type="monotone" dataKey="waist" stroke="var(--bw-danger)" strokeWidth={2} dot={{ r: 3 }} name="Cintura (cm)" />
                </LineChart>
              </ResponsiveContainer>
            </CorpoCard>
          )}
        </div>
      )}

      {/* Correlation: weight vs training volume */}
      {correlationData.length > 2 && (
        <CorpoCard title="CORRELACIÓN: PESO vs VOLUMEN DE ENTRENAMIENTO">
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={correlationData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--bw-border)" />
              <XAxis dataKey="week" tick={{ fontSize: 9, fill: "var(--bw-muted)" }} tickFormatter={(w: string) => w.split("-W")[1] ?? w} />
              <YAxis yAxisId="weight" tick={{ fontSize: 10, fill: "var(--bw-muted)" }} domain={["dataMin - 5", "dataMax + 5"]} />
              <YAxis yAxisId="volume" orientation="right" tick={{ fontSize: 10, fill: "var(--bw-muted)" }} tickFormatter={formatVolume} />
              <Tooltip contentStyle={{ background: "var(--bw-surface)", border: "1px solid var(--bw-border)", color: "var(--bw-fg)", fontSize: 11 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Line yAxisId="weight" type="monotone" dataKey="weight" stroke="var(--bw-accent)" strokeWidth={2} dot={false} name="Peso (lbs)" />
              <Line yAxisId="volume" type="monotone" dataKey="volume" stroke="var(--bw-success)" strokeWidth={1} strokeDasharray="5 5" dot={false} name="Volumen" />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-[10px] text-[var(--bw-muted)] mt-2">
            Peso corporal (eje izq.) vs volumen de entrenamiento semanal (eje der.). Útil para identificar si más volumen correlaciona con cambios de peso.
          </p>
        </CorpoCard>
      )}

      {/* Extra metrics: HR and sleep */}
      {weightTrend.some((w) => w.restHR || w.sleep) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {weightTrend.some((w) => w.restHR) && (
            <CorpoCard title="FRECUENCIA CARDÍACA EN REPOSO">
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={weightTrend.filter((w) => w.restHR)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--bw-border)" />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--bw-muted)" }} />
                  <YAxis tick={{ fontSize: 10, fill: "var(--bw-muted)" }} domain={["dataMin - 5", "dataMax + 5"]} />
                  <Tooltip contentStyle={{ background: "var(--bw-surface)", border: "1px solid var(--bw-border)", color: "var(--bw-fg)", fontSize: 11 }} />
                  <Line type="monotone" dataKey="restHR" stroke="var(--bw-danger)" strokeWidth={2} dot={{ r: 3 }} name="BPM" />
                </LineChart>
              </ResponsiveContainer>
            </CorpoCard>
          )}

          {weightTrend.some((w) => w.sleep) && (
            <CorpoCard title="HORAS DE SUEÑO">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={weightTrend.filter((w) => w.sleep)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--bw-border)" />
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--bw-muted)" }} />
                  <YAxis tick={{ fontSize: 10, fill: "var(--bw-muted)" }} domain={[0, 12]} />
                  <Tooltip contentStyle={{ background: "var(--bw-surface)", border: "1px solid var(--bw-border)", color: "var(--bw-fg)", fontSize: 11 }} />
                  <Bar dataKey="sleep" fill="var(--bw-accent)" opacity={0.6} name="Horas" />
                </BarChart>
              </ResponsiveContainer>
            </CorpoCard>
          )}
        </div>
      )}

      {/* History table */}
      {biometrics.length > 0 && (
        <CorpoCard title="HISTORIAL DE MEDIDAS">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[var(--bw-muted)] uppercase border-b border-[var(--bw-border)]">
                  <th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3">Peso</th>
                  <th className="py-2 pr-3">BF%</th>
                  <th className="py-2 pr-3">Cintura</th>
                  <th className="py-2 pr-3">Cuello</th>
                  <th className="py-2 pr-3">Pecho</th>
                  <th className="py-2 pr-3">Ombligo</th>
                  <th className="py-2 pr-3">Brazo I/D</th>
                  <th className="py-2 pr-3">Pierna I/D</th>
                  <th className="py-2 pr-3">FC</th>
                  <th className="py-2 pr-3">Sueño</th>
                  <th className="py-2">Notas</th>
                </tr>
              </thead>
              <tbody>
                {biometrics.slice(0, 30).map((b) => (
                  <tr key={b.id} className="border-b border-[var(--bw-border)] last:border-0">
                    <td className="py-1.5 pr-3 text-[var(--bw-accent)] font-mono">{b.date}</td>
                    <td className="py-1.5 pr-3 text-[var(--bw-fg)]">{b.weight_kg ? `${lbsFromKg(b.weight_kg)} lbs` : "—"}</td>
                    <td className="py-1.5 pr-3 text-[var(--bw-fg)]">{b.body_fat_pct ? `${b.body_fat_pct}%` : "—"}</td>
                    <td className="py-1.5 pr-3 text-[var(--bw-fg)]">{b.waist_cm ? `${b.waist_cm}` : "—"}</td>
                    <td className="py-1.5 pr-3 text-[var(--bw-fg)]">{b.neck_cm ? `${b.neck_cm}` : "—"}</td>
                    <td className="py-1.5 pr-3 text-[var(--bw-fg)]">{b.chest_cm ? `${b.chest_cm}` : "—"}</td>
                    <td className="py-1.5 pr-3 text-[var(--bw-fg)]">{b.navel_cm ? `${b.navel_cm}` : "—"}</td>
                    <td className="py-1.5 pr-3 text-[var(--bw-fg)]">{b.arm_l_cm || b.arm_r_cm ? `${b.arm_l_cm ?? "—"}/${b.arm_r_cm ?? "—"}` : "—"}</td>
                    <td className="py-1.5 pr-3 text-[var(--bw-fg)]">{b.leg_l_cm || b.leg_r_cm ? `${b.leg_l_cm ?? "—"}/${b.leg_r_cm ?? "—"}` : "—"}</td>
                    <td className="py-1.5 pr-3 text-[var(--bw-fg)]">{b.resting_hr ?? "—"}</td>
                    <td className="py-1.5 pr-3 text-[var(--bw-fg)]">{b.sleep_hours ? `${b.sleep_hours}h` : "—"}</td>
                    <td className="py-1.5 text-[var(--bw-muted)] truncate max-w-[150px]">{b.notes ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CorpoCard>
      )}

      {biometrics.length === 0 && (
        <CorpoCard>
          <div className="text-center py-8">
            <Ruler size={32} className="mx-auto text-[var(--bw-muted)] mb-3" />
            <p className="text-sm text-[var(--bw-muted)]">Sin medidas registradas</p>
            <p className="text-xs text-[var(--bw-muted)] mt-1">Registra tu peso, body fat, cintura y más</p>
          </div>
        </CorpoCard>
      )}
    </div>
  );
}

// ============================================================================
// Main Route
// ============================================================================

export function GymRoute() {
  const [tab, setTab] = useState<Tab>("dashboard");

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: "dashboard", label: "Dashboard", icon: <Activity size={14} /> },
    { id: "progression", label: "Progresión", icon: <TrendingUp size={14} /> },
    { id: "medidas", label: "Medidas", icon: <Ruler size={14} /> },
    { id: "import", label: "Import", icon: <Upload size={14} /> },
  ];

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <CRTHeader text="Gym Analytics" level={2} />
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-[var(--bw-border)] pb-1 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`
              flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-wider
              transition-colors border-b-2 whitespace-nowrap
              ${tab === t.id
                ? "text-[var(--bw-accent)] border-b-[var(--bw-accent)]"
                : "text-[var(--bw-muted)] border-b-transparent hover:text-[var(--bw-fg)]"
              }
            `}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "dashboard" && <DashboardTab />}
      {tab === "progression" && <ProgressionTab />}
      {tab === "medidas" && <MedidasTab />}
      {tab === "import" && <ImportTab />}
    </div>
  );
}
